import { FC, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Button } from '@factorialco/deprecated-design-system'
import { PayrollSendToBookkeeperDialogDocument } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { isTerminalStatus } from 'modules/payroll_bundle/lib/Incidences/Status'

import { InstanceProps } from 'modules/payroll_bundle/pages/IncidencesPage/components/_StatusChangeDialog/types'
import Content from './Content'

const SendToBookeeperDialog: FC<InstanceProps> = ({
  incidences = [],
  syncing,
  results,
  onDismiss,
  onCommit,
}) => {
  const filteredIncidenceIds = new Set(
    incidences.filter((incidence) => !isTerminalStatus(incidence.status)).map((i) => i.id)
  )
  const [ignoreWarnings, setIgnoreWarnings] = useState(false)

  const { loading: loadingRuleEvaluations, data } = useQuery(
    PayrollSendToBookkeeperDialogDocument,
    {
      variables: {
        ids: [...filteredIncidenceIds],
      },
    }
  )
  const evaluatedIncidences = data?.bookkeepersManagement?.incidencesConnection.nodes

  const validIncidenceIds: Set<number> = new Set()
  const warningIncidenceIds: Set<number> = new Set()

  evaluatedIncidences?.forEach(({ id: incidenceId, ruleEvaluation }) => {
    switch (ruleEvaluation?.validity) {
      case 'valid':
        incidenceId && validIncidenceIds.add(incidenceId)
      case 'warning':
        incidenceId && warningIncidenceIds.add(incidenceId)
    }
  })

  const disableAction =
    syncing ||
    loadingRuleEvaluations ||
    (!validIncidenceIds.size && !(warningIncidenceIds.size && ignoreWarnings))

  const success = results?.success

  return (
    <Modal
      illustration="send-email-envelope"
      title={i18n.t('bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.modal_title')}
      description={
        results?.success === false ? undefined : (
          <Content
            loadingRuleEvaluations={loadingRuleEvaluations}
            results={results}
            ignoreWarnings={ignoreWarnings}
            setIgnoreWarnings={setIgnoreWarnings}
            incidences={evaluatedIncidences}
            filteredIncidenceIds={filteredIncidenceIds}
            syncing={syncing}
          />
        )
      }
      onClose={onDismiss}
      footer={
        <>
          <Button type="secondary" onClick={onDismiss} disabled={syncing}>
            {i18n.t('common.cancel')}
          </Button>
          {success ? (
            <Button type="positive" onClick={onDismiss}>
              {i18n.t('common.done')}
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={() => {
                onCommit(
                  ignoreWarnings
                    ? new Set([...validIncidenceIds, ...warningIncidenceIds])
                    : validIncidenceIds,
                  ignoreWarnings
                )
                track(trackingEvents.compensations.sendToBookkeeperAttempted, {
                  incidenceCount: ignoreWarnings
                    ? validIncidenceIds.size + warningIncidenceIds.size
                    : validIncidenceIds.size,
                  ignoreWarnings,
                })
              }}
              disabled={disableAction}
            >
              {i18n.t('bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.action')}
            </Button>
          )}
        </>
      }
    >
      {results?.errorMessages && !syncing && results.success && (
        <Callout
          type="warning"
          description={i18n.t('bookkeepers.incidence.bulk_status_changes.partial_success')}
        />
      )}
      {results && !results.success && !syncing && (
        <Callout
          type="critical"
          description={i18n.t('bookkeepers.incidence.bulk_status_changes.dialog_error')}
        />
      )}
    </Modal>
  )
}

export default SendToBookeeperDialog
